import { CheckCircle2, Loader2, LockKeyhole, LogOut, UserRound, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { apiUrl } from "./api";
import "./auth.css";

type AuthMode = "login" | "register";
type AuthUser = {
  id: string;
  fullName: string;
  email: string;
  emailVerified: boolean;
  provider: "password" | "google";
};

type GoogleCredentialResponse = { credential: string };
type GoogleIdentity = {
  initialize: (config: { client_id: string; callback: (response: GoogleCredentialResponse) => void }) => void;
  renderButton: (element: HTMLElement, options: Record<string, string | number>) => void;
  disableAutoSelect: () => void;
};

declare global {
  interface Window {
    google?: { accounts: { id: GoogleIdentity } };
  }
}

let googleScriptPromise: Promise<void> | null = null;
let initializedGoogleClientId: string | null = null;

function loadGoogleIdentity() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-amerikolay-google]");
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Google giriş servisi yüklenemedi.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.amerikolayGoogle = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google giriş servisi yüklenemedi."));
    document.head.appendChild(script);
  });

  return googleScriptPromise;
}

async function authRequest(path: string, options?: RequestInit) {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    ...options,
    headers: options?.body ? { "Content-Type": "application/json" } : options?.headers,
  });

  if (response.status === 204) return null;
  const body = await response.json().catch(() => null) as { error?: string; title?: string; user?: AuthUser } | null;
  if (!response.ok) {
    throw new Error(body?.error ?? body?.title ?? (response.status === 401 ? "E-posta veya şifre hatalı." : "İşlem tamamlanamadı."));
  }
  return body;
}

export default function AuthOverlay() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode>("login");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleHandlerRef = useRef<(credential: string) => void>(() => undefined);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim() ?? "";

  useEffect(() => {
    const captureAuthTriggers = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.closest(".auth-overlay")) return;
      const button = target.closest("button");
      const label = button?.textContent?.replace(/\s+/g, " ").trim() ?? "";
      const shouldOpen = label.startsWith("Giriş yap") || label.startsWith("Plus'ı dene") || label.startsWith("Yol haritamı kaydet");
      if (!button || !shouldOpen) return;

      event.preventDefault();
      event.stopPropagation();
      setMode("login");
      setError(null);
      setOpen(true);
    };

    document.addEventListener("click", captureAuthTriggers, true);
    return () => document.removeEventListener("click", captureAuthTriggers, true);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    void authRequest("/api/v1/auth/me")
      .then((body) => setUser(body?.user ?? null))
      .catch(() => setUser(null));
  }, []);

  async function handleGoogleCredential(credential: string) {
    setBusy(true);
    setError(null);
    try {
      const body = await authRequest("/api/v1/auth/google", {
        method: "POST",
        body: JSON.stringify({ credential }),
      });
      setUser(body?.user ?? null);
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "Google ile giriş tamamlanamadı.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    googleHandlerRef.current = handleGoogleCredential;
  });

  useEffect(() => {
    if (!open || user || !googleClientId || !googleButtonRef.current) return;

    let cancelled = false;
    void loadGoogleIdentity()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current) return;
        if (initializedGoogleClientId !== googleClientId) {
          window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: (response) => googleHandlerRef.current(response.credential),
          });
          initializedGoogleClientId = googleClientId;
        }
        googleButtonRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "rectangular",
          text: mode === "register" ? "signup_with" : "signin_with",
          locale: "tr",
          width: Math.min(360, googleButtonRef.current.clientWidth || 360),
        });
      })
      .catch((exception) => {
        if (!cancelled) setError(exception instanceof Error ? exception.message : "Google giriş servisi yüklenemedi.");
      });

    return () => { cancelled = true; };
  }, [googleClientId, mode, open, user]);

  async function submitCredentials(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const data = Object.fromEntries(new FormData(event.currentTarget));

    try {
      const path = mode === "register" ? "/api/v1/auth/register" : "/api/v1/auth/login";
      const body = await authRequest(path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      setUser(body?.user ?? null);
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "Giriş tamamlanamadı.");
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    setBusy(true);
    setError(null);
    try {
      await authRequest("/api/v1/auth/logout", { method: "POST" });
      window.google?.accounts?.id.disableAutoSelect();
      setUser(null);
      setMode("login");
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "Çıkış yapılamadı.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="auth-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
      <section className="auth-card" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="auth-close" onClick={() => setOpen(false)} aria-label="Pencereyi kapat"><X /></button>

        {user ? (
          <div className="auth-account">
            <div className="auth-icon auth-icon-success"><CheckCircle2 /></div>
            <span className="auth-kicker">HESABIN HAZIR</span>
            <h2 id="auth-title">Hoş geldin, {user.fullName.split(" ")[0]}.</h2>
            <p>Oturumun güvenli şekilde açıldı. Yol haritanı ve başvurularını bu hesapla sürdürebilirsin.</p>
            <div className="auth-user-card">
              <div><UserRound /></div>
              <span><strong>{user.fullName}</strong><small>{user.email}</small></span>
              <em>{user.provider === "google" ? "Google" : "E-posta"}</em>
            </div>
            <button className="auth-primary" onClick={() => setOpen(false)}>Devam et</button>
            <button className="auth-secondary" onClick={logout} disabled={busy}><LogOut /> Çıkış yap</button>
          </div>
        ) : (
          <>
            <div className="auth-icon"><LockKeyhole /></div>
            <span className="auth-kicker">AMERİKOLAY HESABI</span>
            <h2 id="auth-title">{mode === "login" ? "Yol haritana devam et." : "Ücretsiz hesabını oluştur."}</h2>
            <p>{mode === "login" ? "Profilini, başvurularını ve son tarihlerini tek yerde güvenle takip et." : "Kaydettiğin yolları ve başvuruları cihazlar arasında güvenle sürdürebilirsin."}</p>

            <div className="auth-tabs" role="tablist" aria-label="Hesap işlemleri">
              <button className={mode === "login" ? "active" : ""} onClick={() => { setMode("login"); setError(null); }}>Giriş yap</button>
              <button className={mode === "register" ? "active" : ""} onClick={() => { setMode("register"); setError(null); }}>Kayıt ol</button>
            </div>

            <form className="auth-form" onSubmit={submitCredentials}>
              {mode === "register" && <label>Ad soyad<input name="fullName" required autoComplete="name" maxLength={200} placeholder="Adın ve soyadın" /></label>}
              <label>E-posta<input name="email" required type="email" autoComplete="email" maxLength={320} placeholder="sen@eposta.com" /></label>
              <label>Şifre<input name="password" required type="password" minLength={10} maxLength={128} autoComplete={mode === "register" ? "new-password" : "current-password"} placeholder={mode === "register" ? "En az 10 karakter" : "Şifren"} /></label>
              {error && <p className="auth-error" role="alert">{error}</p>}
              <button className="auth-primary" disabled={busy}>
                {busy && <Loader2 className="auth-spinner" />}
                {busy ? "İşleniyor..." : mode === "login" ? "Giriş yap" : "Hesap oluştur"}
              </button>
            </form>

            {googleClientId && (
              <>
                <div className="auth-divider"><span>veya</span></div>
                <div className="auth-google" ref={googleButtonRef} aria-label="Google ile giriş" />
              </>
            )}

            <small className="auth-note"><LockKeyhole /> Oturum bilgilerin HttpOnly güvenli çerezde tutulur.</small>
          </>
        )}
      </section>
    </div>
  );
}
